import { useMemo, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Plus } from "lucide-react";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennCheckbox, PennDropdown } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import type { ProductFormData } from "@/types/admin/product.types";
import { productSchema } from "@/types/admin/product.types";
import {
  useCostModels,
  useCreateProductAdmin,
  useDeleteProductAdmin,
  useProductAdmin,
  useProductCategories,
  useProductSubcategories,
  useProductsAdmin,
  useUpdateProductAdmin,
} from "@/hooks/useProductsAdmin";
import type {
  CreateProductAdminBody,
  ProductAdmin,
  UpdateProductAdminBody,
} from "@/api/productsAdmin";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ProductAdmin } | null;

const blank: ProductFormData = {
  id: "", row: 0, category: "", subcategory: "", name: "", link: "",
  responsibility: "",
  supports: { pemClinch: false, blindRivet: false, profile: false, other: false },
  maxSizeLabel: "", maxSize: 0,
  sizes: { maxMetricNutSize: 0, maxMetricStudSize: 0 },
  maxForceKn: 0,
  features: { processMonitoring: false, robotIntegration: false },
  availability: { EU: false, NA: false, ASIA: false },
  costModel: "", imageLink: "", keyCharacteristics: [{ value: "" }],
};

/** Inline checkbox + label pair that matches the field text style. */
const CheckItem = ({
  label,
  disabled,
  ...rest
}: { label: string; disabled?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <label className={`flex items-center gap-2 text-body select-none ${disabled ? "text-blue_grey4 cursor-default" : "text-blue_grey cursor-pointer"}`}>
    <PennCheckbox {...rest} disabled={disabled} />
    {label}
  </label>
);

/** Map a server `ProductAdmin` row into the nested form shape. */
const productToFormData = (p: ProductAdmin): ProductFormData => ({
  id: p.productId,
  row: p.rowNumber ?? 0,
  category: p.category ?? "",
  subcategory: p.subcategory ?? "",
  name: p.productName ?? "",
  link: p.link ?? "",
  responsibility: p.responsibility ?? "",
  supports: {
    pemClinch: !!p.supportsPemClinch,
    blindRivet: !!p.supportsBlindRivet,
    profile: !!p.supportsProfile,
    other: !!p.supportsOther,
  },
  maxSizeLabel: p.maxSizeLabel ?? "",
  maxSize: p.maxSize ?? 0,
  sizes: {
    maxMetricNutSize: p.sizesMaxMetricNutSize ?? 0,
    maxMetricStudSize: p.sizesMaxMetricStudSize ?? 0,
  },
  maxForceKn: p.maxForceKn ? Number(p.maxForceKn) : 0,
  features: {
    processMonitoring: !!p.featuresProcessMonitoring,
    robotIntegration: !!p.featuresRobotIntegration,
  },
  availability: {
    EU: !!p.availabilityEu,
    NA: !!p.availabilityNa,
    ASIA: !!p.availabilityAsia,
  },
  costModel: p.modelId ?? "",
  imageLink: p.imageLink ?? "",
  keyCharacteristics:
    p.keyCharacteristics.length > 0
      ? p.keyCharacteristics.map((value) => ({ value }))
      : [{ value: "" }],
});

/** Map the form payload back to the API body. */
const formDataToBody = (
  form: ProductFormData,
): Omit<CreateProductAdminBody, "productId"> => {
  const keyCharacteristics: string[] = (form.keyCharacteristics ?? [])
    .map((r) => r.value.trim())
    .filter((v) => v.length > 0);
  return {
    rowNumber: form.row,
    category: form.category || null,
    subcategory: form.subcategory || null,
    productName: form.name || null,
    link: form.link || null,
    responsibility: form.responsibility?.trim() || null,
    supportsPemClinch: form.supports.pemClinch,
    supportsBlindRivet: form.supports.blindRivet,
    supportsProfile: form.supports.profile,
    supportsOther: form.supports.other,
    maxSizeLabel: form.maxSizeLabel || null,
    maxSize: form.maxSize ?? null,
    sizesMaxMetricNutSize: form.sizes.maxMetricNutSize ?? null,
    sizesMaxMetricStudSize: form.sizes.maxMetricStudSize ?? null,
    maxForceKn: form.maxForceKn != null ? String(form.maxForceKn) : null,
    featuresProcessMonitoring: form.features.processMonitoring,
    featuresRobotIntegration: form.features.robotIntegration,
    availabilityEu: form.availability.EU,
    availabilityNa: form.availability.NA,
    availabilityAsia: form.availability.ASIA,
    modelId: form.costModel || null,
    imageLink: form.imageLink || null,
    keyCharacteristics,
  };
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

export const ProductsPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const listParams = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, search: search || undefined }),
    [pageIndex, pageSize, search],
  );
  const { data, isLoading, isFetching, isError, error } = useProductsAdmin(listParams);
  const items: ProductAdmin[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductAdmin | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingProductId = modal?.mode !== "add" ? (modal?.item?.productId ?? null) : null;
  const detail = useProductAdmin(editingProductId);

  const createMutation = useCreateProductAdmin();
  const updateMutation = useUpdateProductAdmin();
  const deleteMutation = useDeleteProductAdmin();

  const { register, control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
  });

  const { fields: kcFields, append: kcAppend, remove: kcRemove } = useFieldArray({
    control,
    name: "keyCharacteristics",
  });

  const selectedCategory = watch("category");

  // Dropdown sources — populated from svc-04
  const categoriesQuery = useProductCategories();
  const subcategoriesQuery = useProductSubcategories(selectedCategory || undefined);
  const costModelsQuery = useCostModels();

  const categoryOptions = useMemo(
    () => (categoriesQuery.data ?? []).map((c) => ({ value: c.categoryId, label: c.name })),
    [categoriesQuery.data],
  );
  const subcategoryOptions = useMemo(
    () => (subcategoriesQuery.data ?? []).map((s) => ({ value: s.subcategoryId, label: s.name })),
    [subcategoriesQuery.data],
  );
  const costModelOptions = useMemo(
    () => [
      { value: "", label: "(none)" },
      ...(costModelsQuery.data ?? []).map((m) => ({
        value: m.modelId,
        label: m.label ? `${m.modelId} — ${m.label}` : m.modelId,
      })),
    ],
    [costModelsQuery.data],
  );

  const openAdd = () => { setFormError(null); reset(blank); setModal({ mode: "add" }); };
  const openEdit = (item: ProductAdmin) => { setFormError(null); reset(productToFormData(item)); setModal({ mode: "edit", item }); };
  const openView = (item: ProductAdmin) => { setFormError(null); reset(productToFormData(item)); setModal({ mode: "view", item }); };

  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: ProductFormData) => {
    setFormError(null);
    const body = formDataToBody(form);

    if (modal?.mode === "edit" && modal.item) {
      const updateBody: UpdateProductAdminBody = body;
      updateMutation.mutate(
        { productId: modal.item.productId, body: updateBody },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save product.")),
        },
      );
    } else {
      const createBody: CreateProductAdminBody = { productId: form.id.trim(), ...body };
      createMutation.mutate(createBody, {
        onSuccess: closeModal,
        onError: (e) => setFormError(apiErrorMessage(e, "Failed to create product.")),
      });
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ProductAdmin>[] = [
    { header: "ID", render: (i) => i.productId },
    { header: "Row", render: (i) => i.rowNumber ?? "—" },
    { header: "Category", render: (i) => i.category ?? "—" },
    { header: "Subcategory", render: (i) => i.subcategory ?? "—" },
    { header: "Name", render: (i) => i.productName ?? "—" },
    { header: "Link", render: (i) => i.link ? <a href={i.link} target="_blank" rel="noreferrer" className="text-penn-500 underline text-small">link</a> : null },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: "Add Products record", edit: "Edit Products record", view: "View Products record" };

  return (
    <>
      <PageHeader title="Dataset: Products" subtitle={`Record-level editor for products (id: id). · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load products{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search ID…"
      />

      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i.productId} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={items.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
        <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}
          {/* ── Core fields ── */}
          <PennField label="ID" htmlFor="id" required={!isView} error={!isView ? errors.id?.message : undefined}>
            <PennInput id="id" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.id} {...register("id")} />
          </PennField>
          <PennField label="Row" htmlFor="row" required={!isView} error={!isView ? errors.row?.message : undefined}>
            <PennInput id="row" type="number" disabled={isView} error={!isView && !!errors.row} {...register("row")} />
          </PennField>

          <PennField label="Category" htmlFor="category" required={!isView} error={!isView ? errors.category?.message : undefined}>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <PennDropdown
                  inputId="category"
                  options={categoryOptions}
                  value={field.value ?? ""}
                  onChange={(v) => { field.onChange(v); setValue("subcategory", "", { shouldValidate: false }); }}
                  onBlur={field.onBlur}
                  isSearchable
                  isDisabled={isView}
                  error={!isView && !!errors.category}
                  placeholder={categoriesQuery.isLoading ? "Loading…" : "Select category…"}
                />
              )}
            />
          </PennField>
          <PennField label="Subcategory" htmlFor="subcategory" required={!isView} error={!isView ? errors.subcategory?.message : undefined}>
            <Controller
              name="subcategory"
              control={control}
              render={({ field }) => (
                <PennDropdown
                  inputId="subcategory"
                  options={subcategoryOptions}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  isSearchable
                  isDisabled={isView || !selectedCategory}
                  error={!isView && !!errors.subcategory}
                  placeholder={selectedCategory ? (subcategoriesQuery.isLoading ? "Loading…" : "Select subcategory…") : "Select a category first"}
                />
              )}
            />
          </PennField>

          <PennField label="Name" htmlFor="name" required={!isView} error={!isView ? errors.name?.message : undefined}>
            <PennInput id="name" disabled={isView} error={!isView && !!errors.name} {...register("name")} />
          </PennField>
          <PennField label="Link" htmlFor="link" error={!isView ? errors.link?.message : undefined}>
            <PennInput id="link" disabled={isView} error={!isView && !!errors.link} {...register("link")} />
          </PennField>

          <PennField label="Responsibility" htmlFor="responsibility" error={!isView ? errors.responsibility?.message : undefined} className="col-span-1 sm:col-span-2">
            <PennInput id="responsibility" disabled={isView} {...register("responsibility")} />
          </PennField>

          {/* ── Supports (checkboxes) ── */}
          <PennField label="Supports" htmlFor="supports" className="col-span-1 sm:col-span-2">
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1">
              <CheckItem label="PEM Clinch" disabled={isView} {...register("supports.pemClinch")} />
              <CheckItem label="Blind Rivet" disabled={isView} {...register("supports.blindRivet")} />
              <CheckItem label="Profile" disabled={isView} {...register("supports.profile")} />
              <CheckItem label="Other" disabled={isView} {...register("supports.other")} />
            </div>
          </PennField>

          {/* ── Size fields ── */}
          <PennField label="Max Size Label" htmlFor="maxSizeLabel" error={!isView ? errors.maxSizeLabel?.message : undefined}>
            <PennInput id="maxSizeLabel" disabled={isView} {...register("maxSizeLabel")} />
          </PennField>
          <PennField label="Max Size" htmlFor="maxSize" error={!isView ? errors.maxSize?.message : undefined}>
            <PennInput id="maxSize" type="number" disabled={isView} error={!isView && !!errors.maxSize} {...register("maxSize")} />
          </PennField>

          <PennField label="Max Metric Nut Size" htmlFor="sizes.maxMetricNutSize" error={!isView ? errors.sizes?.maxMetricNutSize?.message : undefined}>
            <PennInput id="sizes.maxMetricNutSize" type="number" disabled={isView} {...register("sizes.maxMetricNutSize")} />
          </PennField>
          <PennField label="Max Metric Stud Size" htmlFor="sizes.maxMetricStudSize" error={!isView ? errors.sizes?.maxMetricStudSize?.message : undefined}>
            <PennInput id="sizes.maxMetricStudSize" type="number" disabled={isView} {...register("sizes.maxMetricStudSize")} />
          </PennField>

          <PennField label="Max Force (kN)" htmlFor="maxForceKn" error={!isView ? errors.maxForceKn?.message : undefined}>
            <PennInput id="maxForceKn" type="number" step="0.01" disabled={isView} error={!isView && !!errors.maxForceKn} {...register("maxForceKn")} />
          </PennField>

          {/* ── Cost Model (dropdown) ── */}
          <PennField label="Cost Model" htmlFor="costModel" error={!isView ? errors.costModel?.message : undefined}>
            <Controller
              name="costModel"
              control={control}
              render={({ field }) => (
                <PennDropdown
                  inputId="costModel"
                  options={costModelOptions}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  isSearchable
                  isDisabled={isView}
                  error={!isView && !!errors.costModel}
                  placeholder={costModelsQuery.isLoading ? "Loading…" : "Select cost model…"}
                />
              )}
            />
          </PennField>

          <PennField label="Image Link" htmlFor="imageLink" error={!isView ? errors.imageLink?.message : undefined} className="col-span-1 sm:col-span-2">
            <PennInput id="imageLink" disabled={isView} {...register("imageLink")} />
          </PennField>

          {/* ── Features (checkboxes) ── */}
          <PennField label="Features" htmlFor="features" className="col-span-1 sm:col-span-2">
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1">
              <CheckItem label="Process Monitoring" disabled={isView} {...register("features.processMonitoring")} />
              <CheckItem label="Robot Integration" disabled={isView} {...register("features.robotIntegration")} />
            </div>
          </PennField>

          {/* ── Availability (checkboxes) ── */}
          <PennField label="Availability" htmlFor="availability" className="col-span-1 sm:col-span-2">
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1">
              <CheckItem label="EU" disabled={isView} {...register("availability.EU")} />
              <CheckItem label="NA" disabled={isView} {...register("availability.NA")} />
              <CheckItem label="ASIA" disabled={isView} {...register("availability.ASIA")} />
            </div>
          </PennField>

          {/* ── Key Characteristics (dynamic row list) ── */}
          <PennField
            label="Key Characteristics"
            htmlFor="keyCharacteristics.0.value"
            hint={isView ? undefined : "One value per row"}
            error={!isView ? errors.keyCharacteristics?.message : undefined}
            className="col-span-1 sm:col-span-2"
          >
            <div className="space-y-2">
              {kcFields.map((row, idx) => (
                <div key={row.id} className="flex items-center gap-2">
                  <PennInput
                    id={`keyCharacteristics.${idx}.value`}
                    disabled={isView}
                    placeholder={`Value ${idx + 1}`}
                    {...register(`keyCharacteristics.${idx}.value` as const)}
                  />
                  {!isView && (
                    <button
                      type="button"
                      aria-label={`Remove key characteristic ${idx + 1}`}
                      onClick={() => {
                        if (kcFields.length === 1) {
                          setValue(`keyCharacteristics.0.value`, "", { shouldValidate: false });
                        } else {
                          kcRemove(idx);
                        }
                      }}
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-800 text-blue_grey3 hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              {!isView && (
                <button
                  type="button"
                  onClick={() => kcAppend({ value: "" })}
                  className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-3 py-2 text-blue_grey2 hover:border-penn-500 hover:text-penn-500 hover:bg-penn-50 transition text-body"
                >
                  <Plus size={14} /> Add Row
                </button>
              )}
            </div>
          </PennField>
        </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.productId, {
            onError: (e) => setDeleteError(apiErrorMessage(e, "Failed to delete product.")),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.productName ?? deleteTarget?.productId}
      />
    </>
  );
};
